import { useLazyQuery, useTranslation } from '@gimlite/common';
import {
  ListFlatMultipleCheckType,
  Widget,
  WizardItemType,
  Zone,
} from '@gimlite/ui-v1';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ContractEntity,
  Query,
  QueryContractsArgs,
  QueryGlobalSearchArgs,
} from '../../../../client/graphql';
import { contractsGql } from '../../../../common/gql/contracts.gql';
import { globalSearchGQL } from '../../../../common/gql/globalSearch.gql';
import { SwitcherText } from '../../../../components/switcher-text/switcher-text.component';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import { ContractsCreateForm } from '../../../../form/contracts-create/contracts-create.form';
import {
  MultipleCheckForm,
  MultipleCheckFormType,
} from '../../../../form/multiple-check/multiple-check.form';
import { CreateNewMasterPoolContractWizardContext } from '../../createNewMasterPoolContract.wizard';

const pageLimit = 50;

export const ParkersStep = ({
  context: {
    contractBase,
    product,
    contracts,
    contractExistedSave,
    contractMode,
    parkingId,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<CreateNewMasterPoolContractWizardContext>) => {
  const { t, lang } = useTranslation();
  const [searchContractsResult, setSearchContractsResult] = useState<
    ContractEntity[] | null
  >(null);

  const [contractsCall, contractsQuery] = useLazyQuery<
    { contracts: Query['contracts'] },
    QueryContractsArgs
  >(contractsGql);

  const [globalSearchCall] = useLazyQuery<
    { globalSearch: Query['globalSearch'] },
    QueryGlobalSearchArgs
  >(globalSearchGQL);

  useEffect(() => {
    (async () => {
      try {
        await contractsCall({
          variables: {
            page: 1,
            limit: pageLimit,
            order: 'name1',
            parkingId: parkingId,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contractBase.startDate!, contractBase.endDate!],
          },
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [contractBase.startDate, contractBase.endDate, parkingId]);

  const contractsResults = useMemo(() => {
    if (!contractsQuery.data) return null;

    return contractsQuery.data?.contracts;
  }, [contractsQuery.data?.contracts]);

  const contractsFormattedLine = useCallback(
    (
      contracts: ContractEntity[],
    ): ListFlatMultipleCheckType.Data.Line<any>[] => {
      return contracts.map(
        ({
          _id,
          name1,
          name2,
          ospContractId,
          isEnabled,
          stopDate,
          startDate,
        }) => {
          return {
            _id,
            items: {
              component: (
                <ContractMotoristLine
                  data={{
                    firstName: name1 || null,
                    lastName: name2 || null,
                    contractId: ospContractId || null,
                    status: statusHelper({
                      stopDate: new Date(stopDate!),
                      startDate: new Date(startDate!),
                      isEnabled: isEnabled,
                    }),
                  }}
                  config={{ action: false }}
                />
              ),
            },
          };
        },
      );
    },
    [],
  );

  const handleSearch = useCallback(
    async (value: string) => {
      if (value.length >= 3 && parkingId) {
        const { data } = await globalSearchCall({
          variables: {
            content: value,
            parkingIds: parkingId ? [parkingId] : undefined,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contractBase.startDate!, contractBase.endDate!],
          },
        });

        if (data?.globalSearch?.contracts) {
          setSearchContractsResult(data?.globalSearch?.contracts);
        }

        submit({
          contractExistedSave: [
            ...contractExistedSave,
            ...(data?.globalSearch?.contracts || []),
          ].reduce((acc: ContractEntity[], { _id, ...rest }) => {
            if (!acc.find((element) => element._id === _id)) {
              return [...acc, { _id, ...rest }];
            }
            return acc;
          }, []),
        });

        try {
          await contractsCall({
            variables: {
              page: 1,
              limit: pageLimit,
              order: 'name1',
              parkingId: parkingId,
              isNotInPeriod: [contractBase.startDate!, contractBase.endDate!],
              categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            },
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        setSearchContractsResult(() => null);
      }
    },
    [parkingId],
  );

  const { fresh, paging: pagingFormatted } = useMemo((): {
    fresh: ContractEntity[] | undefined;
    paging?: MultipleCheckFormType.Props['data']['paging'];
  } => {
    if (!contractsResults?.list) {
      return { fresh: undefined };
    } else {
      const listTarget = !searchContractsResult
        ? contractsResults.list
        : searchContractsResult;

      return {
        fresh: listTarget,
        paging: !searchContractsResult
          ? contractsResults.paging
          : {
              count: searchContractsResult.length,
              current: 1,
              limit: searchContractsResult.length,
            },
      };
    }
  }, [contractsResults, searchContractsResult]);

  useEffect(() => {
    validate(true);
  }, []);

  useEffect(() => {
    if (contractMode === 'exist') {
      const newList = contractExistedSave;

      if (contractsResults?.list) {
        contractsResults.list.forEach(({ _id, ...rest }) => {
          if (
            contractExistedSave.findIndex((element) => element._id === _id) ===
            -1
          ) {
            newList.push({ _id, ...rest });
          }
        });
      }

      submit({
        contractExistedSave: newList,
      });
    }
  }, [contractsResults?.list, contractExistedSave, contractMode]);

  useEffect(() => {
    (async () => {
      try {
        await contractsCall({
          variables: {
            page: 1,
            limit: pageLimit,
            order: 'name1',
            parkingId: parkingId,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contractBase.startDate!, contractBase.endDate!],
          },
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [parkingId]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'form']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            productName: product.id ? t(`product-${product.id}-name`) : null,
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            capacity: contractBase.capacity,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'form' }}>
        <Zone
          config={{
            gap: {
              y: 0,
              x: 0,
            },
            zones: [
              // ['link', '.'],
              ['work', 'work'],
            ],
            rows: ['1fr'],
            columns: ['1fr', '1fr'],
          }}
        >
          {/* <Zone.Area config={{ area: 'link' }}>
            <Widget config={{ shadow: false, full: true }}>
              <SwitcherText<
                CreateNewMasterPoolContractWizardContext['contractMode']
              >
                handleEvent={{
                  mode: async (value) => {
                    try {
                      await contractsCall({
                        variables: {
                          page: 1,
                          limit: pageLimit,
                          order: 'name1',
                          parkingId: parkingId,
                          categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
                          isNotInPeriod: [
                            contractBase.startDate!,
                            contractBase.endDate!,
                          ],
                        },
                      });
                    } catch (error) {
                      console.error(error);
                    }
                    validate(true);
                    submit({
                      contractMode: value,
                      contracts: {
                        checked: [],
                        created: [],
                      },
                      contractExistedSave:
                        value === 'create' ? [] : contractExistedSave,
                    });
                  },
                }}
                data={{ value: contractMode }}
                config={{
                  mode: [
                    {
                      label: t('chooseExistingContracts?'),
                      value: 'create',
                    },
                    {
                      label: t('createContracts?'),
                      value: 'exist',
                    },
                  ],
                }}
              />
            </Widget>
          </Zone.Area> */}

          <Zone.Area config={{ area: 'work' }}>
            <Widget
              config={{
                title:
                  contractMode === 'exist'
                    ? t('listOfExistingContracts')
                    : t('createSeveralMotoristContracts'),
                shadow: false,
              }}
              state={{
                loading:
                  contractMode === 'exist' ? contractsQuery.loading : undefined,
                refetch:
                  contractMode === 'exist' ? contractsQuery.refetch : undefined,
                error:
                  contractMode === 'exist' ? contractsQuery.error : undefined,
                showPermanentRefetch:
                  contractMode === 'exist' ? false : undefined,
              }}
            >
              {contractMode === 'exist' ? (
                <MultipleCheckForm
                  config={{
                    searchPlaceholder: t('searchAContract'),
                  }}
                  handleEvent={{
                    checkList: (list) => {
                      submit({
                        contracts: {
                          ...contracts,
                          checked: list.map(
                            (id) =>
                              [fresh ? fresh : [], contractExistedSave]
                                .flat()
                                .find(
                                  (element) => element._id === id,
                                ) as ContractEntity,
                          ),
                        },
                      });
                    },
                    paging: async (data) => {
                      try {
                        await contractsCall({
                          variables: {
                            page: data.page,
                            limit: data.limit,
                            order: 'name1',
                            parkingId: parkingId,
                            categories: [
                              'PERIOD_SUBSCRIPTION',
                              'MOTORIST_POOL',
                            ],
                            isNotInPeriod: [
                              contractBase.startDate!,
                              contractBase.endDate!,
                            ],
                          },
                        });
                      } catch (error) {
                        console.error(error);
                      }
                    },
                    search: handleSearch,
                  }}
                  data={{
                    paging: pagingFormatted,
                    checked: contracts.checked.map((element) => element._id),
                    fresh: fresh ? contractsFormattedLine(fresh) : undefined,
                    saved: contractsFormattedLine(contractExistedSave),
                  }}
                />
              ) : (
                <ContractsCreateForm
                  config={{
                    validIfEmpty: true,
                  }}
                  handleEvent={{
                    validate,
                    submit: (data) =>
                      submit({ contracts: { ...contracts, created: data } }),
                  }}
                  data={{
                    created: contracts.created,
                  }}
                />
              )}
            </Widget>
          </Zone.Area>
        </Zone>
      </Zone.Area>
    </Zone>
  );
};
